import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { isVideoFile } from '../utils/mediaUtils';
import { CircularProgress } from '@mui/material';
import "../styles/selectPlaces.css"

function TrippinPhotoSelect({ isTagged = false }) {
  const { trippinId, placeName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [photos, setPhotos] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [loading, setLoading] = useState(true);

  const getFilePathFromSignedUrl = (signedUrl) => {
    try {
      const url = new URL(signedUrl);
      let path = decodeURIComponent(url.pathname.substring(1));
  
      // Remove bucket name if it's at the start of the path (e.g., 'tripapplin/')
      const bucketName = 'tripapplin/';
      if (path.startsWith(bucketName)) {
        path = path.replace(bucketName, '');
      }
  
      // Remove legacy trippin_content prefix if present
      const bucketPrefix = 'trippin_content/';
      if (path.startsWith(bucketPrefix)) {
        path = path.replace(bucketPrefix, '');
      }
  
      return path;
    } catch (err) {
      console.error('Invalid signed URL:', signedUrl);
      return null;
    }
  };
  
  useEffect(() => {
    fetchPhotos();
  }, [trippinId, placeName]);
  
  useEffect(() => {
    if (location.state?.selectedPhotos?.[placeName]) {
      setSelectedOrder([...location.state.selectedPhotos[placeName]]);
    }
  }, [location.state, placeName]);
  
  const fetchPhotos = async () => {
    try {
      const endpoint = isTagged
        ? `/trippins/${trippinId}/placesTagged/${placeName}/photos`
        : `/trippins/${trippinId}/places/${placeName}/photos`;
      const res = await axiosInstance.get(endpoint);
      if (Array.isArray(res.data)) {
        setPhotos(res.data);
      }
      else if (res.data.message) {
        setPhotos([]);
      }
    } catch (err) {
      console.error('Error fetching photos:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return  (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: 'black' }} />
      </div>
    );
  }
  
  if (!loading && photos.length === 0) {
    return (
      <div className="loading-container" style={{ height: "unset" }}>
        <p style={{ fontSize: "18px", color: "#d1d5db", textAlign: "center", marginTop: "50px" }}>
          You have posted all photos for this place
        </p>
      </div>
    );
  }
  
  const handleSelectPhoto = (photoUrl) => {
    const filePath = getFilePathFromSignedUrl(photoUrl);
    if (!filePath) return;
  
    setSelectedOrder((prev) => {
      const idx = prev.indexOf(filePath);
      if (idx === -1) {
        return [...prev, filePath]; // Add to end
      }
      else {
        const copy = [...prev];
        copy.splice(idx, 1); // Remove if already selected
        return copy;
      }
    });
  };
  
  const isSelected = (signedUrl) => {
    const filePath = getFilePathFromSignedUrl(signedUrl);
    return filePath && selectedOrder.includes(filePath);
  };

  const handleDone = () => {
    const updatedPhotos = {
      ...(JSON.parse(sessionStorage.getItem('selectedPhotos')) || {}),
      [placeName]: selectedOrder, // Save cleaned file paths
    };
  
    sessionStorage.setItem('selectedPhotos', JSON.stringify(updatedPhotos));
    navigate(-1, { state: { selectedPhotos: updatedPhotos } });
  };
  
  return (
    <div style={{ marginTop: '32px', padding: '1rem' }}>
      <p className='select-photos-title'>Select Photos for <br />  <b>{placeName}</b></p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '5px',
        }}
      >
        {photos.map((p) => {
          return (
            <div
              key={p.signed_url}
              onClick={() => handleSelectPhoto(p.signed_url)}
              style={{
                position: 'relative',
                cursor: 'pointer',
              }}
            >
              {isVideoFile(p.signed_url) ? (
                <video
                  src={p.signed_url}
                  style={{
                    width: isSelected(p.signed_url) ? '100%' : '100%', //calc(100% - 10px)
                    height: 'auto',
                    aspectRatio: '3 / 4',
                    objectFit: 'cover',
                    border: isSelected(p.signed_url) ? 'none' : 'none', //5px solid #2c3e50
                    borderRadius: '6px',
                  }}
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={p.signed_url}
                  alt="placePhoto"
                  style={{
                    width: isSelected(p.signed_url) ? '100%' : '100%', //calc(100% - 10px)
                    height: 'auto',
                    aspectRatio: '3 / 4',
                    objectFit: 'cover',
                    border: isSelected(p.signed_url) ? 'none' : 'none', //5px solid #2c3e50
                    borderRadius: '6px',
                  }}
                />
              )}

              {isSelected(p.signed_url) && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '50%',
                    transform: 'translate(50%, -50%)',
                    background: '#2c3e50',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '2px',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    textAlign: 'center',
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                  }}
                >
                  {selectedOrder.indexOf(getFilePathFromSignedUrl(p.signed_url)) + 1}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button onClick={handleDone} className="next-button">
        Done
      </button>
    </div>
  );
}

export default TrippinPhotoSelect;
